import secureApiService from './secureApiService'
import { UserModel, UserRole } from '@/models/user.model'
import { PaginatedResponse } from '@/models/templates/pagination.model'

interface UserJSON {
  id: string
  email?: string | null
  first_name?: string | null
  last_name?: string | null
  role?: string | null
  avatar?: string | null
  is_active?: boolean | null
  last_login_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  phone?: string | null
  bio?: string | null
  level?: number | null
  stage?: string | null
  program?: {
    id?: string
    title?: string | null
    department?: {
      id?: string
      name?: string | null
    } | null
    faculty?: {
      id?: string
      name?: string | null
    } | null
  } | null
  programme?: {
    id?: string
    title?: string | null
    department?: {
      id?: string
      name?: string | null
    } | null
    faculty?: {
      id?: string
      name?: string | null
    } | null
  } | null
  department?: {
    id?: string
    name?: string | null
  } | string | null
  phone_number?: string | null
  is_password_temporary?: boolean | null
  student_id?: string | null
  enrollment_date?: string | null
  programme_id?: string | null
  programme_cohort_id?: string | null
  programme_cohort_assigned_at?: string | null
  programme_cohort_completed_at?: string | null
  programmeCohortAssignedAt?: string | null
  programmeCohortCompletedAt?: string | null
  faculty?: {
    id?: string
    name?: string | null
  } | null
}

interface UsersResponseJSON {
  success: boolean
  message?: string
  data?: UserJSON[] | UserJSON | null
  meta?: {
    total?: number
    page?: number
    perPage?: number
    totalPages?: number
  }
}

export interface UsersQueryParams {
  page?: number
  perPage?: number
  search?: string
  role?: UserRole | string
  isActive?: boolean
}

interface UserResponseJSON {
  success: boolean
  message?: string
  data?: UserJSON | null
}

export interface UpdateUserPayload {
  firstName?: string
  lastName?: string
  email?: string
  role?: UserRole | string
  departmentIds?: string[]
  programmeId?: string | null
  programmeCohortId?: string | null
  programmeCohortAssignedAt?: string | null
  programmeCohortCompletedAt?: string | null
  level?: number | null
  stage?: number | string | null
  isActive?: boolean
  phone?: string | null
  bio?: string | null
  title?: string | null
}

class UsersService {
  private basePath = '/users'

  async createUser(data: {
    email: string
    password: string
    firstName: string
    lastName: string
    role: string
  }): Promise<UserModel> {
    const response = await secureApiService.post<{
      success: boolean
      message: string
      data: {
        user: UserJSON
        tokens: any
      }
    }>('/auth/register', data, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Failed to create user')
    }

    // The backend returns { user, tokens }, we map the user part
    return UserModel.fromJSON(payload.data.user)
  }

  async getUsersPage(params: UsersQueryParams = {}): Promise<PaginatedResponse<UserModel>> {
    const searchParams = new URLSearchParams()
    if (params.page) searchParams.set('page', String(params.page))
    if (params.perPage) searchParams.set('perPage', String(params.perPage))
    if (params.search) searchParams.set('search', params.search)
    if (params.role) searchParams.set('role', String(params.role))
    if (typeof params.isActive === 'boolean') {
      searchParams.set('isActive', params.isActive ? 'true' : 'false')
    }

    const endpoint = searchParams.toString() ? `${this.basePath}?${searchParams.toString()}` : this.basePath

    const response = await secureApiService.get<UsersResponseJSON>(endpoint, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch users')
    }

    const usersArray = Array.isArray(payload.data)
      ? payload.data
      : payload.data
        ? [payload.data]
        : []

    const data = usersArray.map((item) => UserModel.fromJSON(item))
    const meta = {
      total: payload.meta?.total ?? data.length,
      page: payload.meta?.page ?? params.page ?? 1,
      perPage: payload.meta?.perPage ?? params.perPage ?? (data.length || 25),
    }

    return { data, meta }
  }

  async getUserById(userId: string): Promise<UserModel> {
    const response = await secureApiService.get<UserResponseJSON>(`${this.basePath}/${userId}`, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Unable to fetch user')
    }

    return UserModel.fromJSON(payload.data)
  }

  async getCurrentUserProfile(): Promise<UserModel> {
    const response = await secureApiService.get<UserResponseJSON>(`${this.basePath}/profile`, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Unable to fetch profile')
    }

    return UserModel.fromJSON(payload.data)
  }

  async updateCurrentUserProfile(payload: UpdateUserPayload): Promise<UserModel> {
    const sanitizedPayload = this.toApiPayload(payload)

    const response = await secureApiService.put<UserResponseJSON>(`${this.basePath}/profile`, sanitizedPayload, {
      requireAuth: true,
    })

    const { data, success, message } = response.data
    if (!success || !data) {
      throw new Error(message || 'Unable to update profile')
    }

    return UserModel.fromJSON(data)
  }

  async updateUser(userId: string, payload: UpdateUserPayload): Promise<UserModel> {
    const sanitizedPayload = this.toApiPayload(payload)

    const response = await secureApiService.put<UserResponseJSON>(`${this.basePath}/${userId}`, sanitizedPayload, {
      requireAuth: true,
    })

    const { data, success, message } = response.data
    if (!success || !data) {
      throw new Error(message || 'Unable to update user')
    }

    return UserModel.fromJSON(data)
  }

  async toggleUserStatus(userId: string, isActive: boolean): Promise<UserModel> {
    return this.updateUser(userId, { isActive })
  }

  async setPaymentBypass(userId: string, bypass: boolean): Promise<void> {
    if (!userId) {
      throw new Error('User ID is required')
    }

    const payload = { bypass }
    const response = await secureApiService.put<{ success: boolean; message?: string }>(
      `/users/${encodeURIComponent(userId)}/payment-bypass`,
      payload,
      { requireAuth: true },
    )

    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Unable to update payment bypass status')
    }
  }

  private toApiPayload(payload: UpdateUserPayload): Record<string, unknown> {
    const transformed: Record<string, unknown> = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      role: payload.role,
      departmentIds: payload.departmentIds,
      programmeId: payload.programmeId,
      programmeCohortId: payload.programmeCohortId,
      programmeCohortAssignedAt: payload.programmeCohortAssignedAt,
      programmeCohortCompletedAt: payload.programmeCohortCompletedAt,
      level: payload.level,
      stage: payload.stage,
      isActive: payload.isActive,
      phone: payload.phone,
      bio: payload.bio,
      title: payload.title,
    }

    Object.keys(transformed).forEach((key) => {
      if (transformed[key] === undefined) {
        delete transformed[key]
      }
    })

    return transformed
  }
}

export const usersService = new UsersService()
